import React, { useState, useEffect, useCallback, useRef } from 'react';
import inventoryService from '../services/inventoryService';
import categoryService from '../services/categoryService';
import supplierService from '../services/supplierService';
import ProductForm from '../components/inventory/ProductForm';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { FiPlusCircle, FiEdit, FiTrash2, FiSearch, FiSliders, FiPrinter } from 'react-icons/fi';
import StockAdjustmentModal from '../components/inventory/StockAdjustmentModal';
import LabelPrintModal from '../components/labels/LabelPrintModal';

const isLikelyObjectId = (value) => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value?.trim?.() || value);

const resolveSupplierName = (product) => {
  if (!product) return '—';
  if (product.primarySupplier?.name) return product.primarySupplier.name;
  if (product.lastSupplier?.name) return product.lastSupplier.name;
  if (product.supplier?.name) return product.supplier.name;
  if (product.supplierName) return product.supplierName;
  if (product.storeLatestSupplierPricing?.supplier?.name) return product.storeLatestSupplierPricing.supplier.name;
  if (product.latestSupplierPricing?.supplier?.name) return product.latestSupplierPricing.supplier.name;
  if (typeof product.supplier === 'string' && !isLikelyObjectId(product.supplier)) {
    return product.supplier;
  }
  if (product.defaultSupplierName) return product.defaultSupplierName;
  if (Array.isArray(product.suppliers) && product.suppliers.length > 0) {
    const named = product.suppliers.find((s) => s?.name);
    if (named) return named.name;
  }
  return '—';
};

const toMoneyNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const resolvePricingDisplay = (product = {}) => {
  const resolvedStorePrice = toMoneyNumber(product.price, 0);
  const resolvedBasePrice = product.basePrice != null
    ? toMoneyNumber(product.basePrice, resolvedStorePrice)
    : resolvedStorePrice;
  const resolvedBaseCost = toMoneyNumber(product.costPrice, 0);
  const resolvedStoreCost = product.storeCost != null
    ? toMoneyNumber(product.storeCost, resolvedBaseCost)
    : resolvedBaseCost;
  return {
    storePrice: resolvedStorePrice,
    basePrice: resolvedBasePrice,
    storeCost: resolvedStoreCost,
    baseCost: resolvedBaseCost,
    hasStorePriceDelta: Math.abs(resolvedStorePrice - resolvedBasePrice) >= 0.001,
    hasStoreCostDelta: Math.abs(resolvedStoreCost - resolvedBaseCost) >= 0.001,
  };
};

const actionHeaderClassName = 'sticky right-0 z-[2] bg-gray-50 px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide shadow-[-10px_0_14px_-14px_rgba(15,23,42,0.55)]';
const actionCellClassName = 'sticky right-0 z-[1] bg-white px-3 py-2 whitespace-nowrap text-right text-sm font-medium shadow-[-10px_0_14px_-14px_rgba(15,23,42,0.55)]';
const compactHeaderClassName = 'px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide';
const compactCellClassName = 'px-3 py-2 text-sm text-slate-600';
const compactNumericCellClassName = 'px-3 py-2 text-sm text-right text-slate-700';
const compactActionButtonClassName = 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50 hover:text-blue-900';

const InventoryPage = () => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [stockModalError, setStockModalError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    subcategory: '',
    supplierId: '',
    noCategory: false,
    noSubcategory: false,
  });
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [labelProduct, setLabelProduct] = useState(null);
  const topInventoryScrollRef = useRef(null);
  const tableInventoryScrollRef = useRef(null);
  const [inventoryTableScrollWidth, setInventoryTableScrollWidth] = useState(0);
  const { user } = useAuth();
  const { activeStore } = useStore();
  const activeStoreId = activeStore?._id;

  const canCreate = user?.permissions?.includes('products:create');
  const canUpdate = user?.permissions?.includes('products:update');
  const canDelete = user?.permissions?.includes('products:delete');
  const canUpdateMasterPrice = user?.permissions?.includes('products:price_master:update');
  const canUpdateMasterCost = user?.permissions?.includes('products:cost_master:update');
  const canUpdateStorePrice = user?.permissions?.includes('products:price_store:update');
  const canUpdateStoreCost = user?.permissions?.includes('products:cost_store:update');
  const canCreateProductWithPricing = canCreate && canUpdateMasterPrice && canUpdateMasterCost;

  const fetchProducts = useCallback(async (search = '', page = 1, activeFilters = filters) => {
    if (!activeStoreId) return;
    try {
      setIsLoading(true);
      const response = await inventoryService.fetchProducts(activeStoreId, {
        page,
        limit: 25,
        search,
        category: activeFilters.category,
        subcategory: activeFilters.subcategory,
        supplierId: activeFilters.supplierId,
        noCategory: activeFilters.noCategory,
        noSubcategory: activeFilters.noSubcategory,
      });
      setProducts(response.data || []);
      const paginationMeta = response.pagination || { page, pages: 1, total: response.data?.length || 0 };
      setPagination(paginationMeta);
      if (page > paginationMeta.pages && paginationMeta.pages > 0) {
        setCurrentPage(paginationMeta.pages);
      }
    } catch (err) {
      setError('Failed to fetch products for this store.');
    } finally {
      setIsLoading(false);
    }
  }, [activeStoreId, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeStoreId]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      fetchProducts(searchTerm, currentPage, filters);
    }, 300);
    return () => clearTimeout(timerId);
  }, [searchTerm, currentPage, filters, fetchProducts]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoryService.fetchCategories();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Failed to load categories for filters', err);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const response = await supplierService.fetchSuppliers({
          compact: true,
          limit: 200,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        const supplierList = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        setSuppliers(supplierList);
      } catch (err) {
        console.warn('Failed to load suppliers for filters', err);
      }
    };
    loadSuppliers();
  }, []);

  const handleOpenModal = (product = null) => {
    setEditingProduct(product);
    setModalError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setModalError('');
  };

  const handleOpenStockModal = (product) => {
    setAdjustingProduct(product);
    setStockModalError('');
    setIsStockModalOpen(true);
  };

  const handleOpenLabelModal = (product) => {
    setLabelProduct(product);
    setIsLabelModalOpen(true);
  };

  const handleSaveProduct = async (productData) => {
    try {
      const dataToSend = { ...productData, storeId: activeStore._id };
      if (!canUpdateMasterPrice) delete dataToSend.price;
      if (!canUpdateMasterCost) delete dataToSend.costPrice;
      if (!canUpdateStorePrice) delete dataToSend.storePriceOverride;
      if (!canUpdateStoreCost) delete dataToSend.storeCost;
      if (editingProduct) {
        await inventoryService.updateProduct(editingProduct._id, dataToSend);
      } else {
        if (!canCreateProductWithPricing) {
          throw new Error('You do not have permission to create products with price/cost values.');
        }
        await inventoryService.createProduct(dataToSend);
      }
      await fetchProducts(searchTerm, currentPage, filters);
      setModalError('');
      handleCloseModal();
    } catch (err) {
      console.error("Save Product Failed:", err);
      setModalError(err.response?.data?.message || 'Failed to save product. Please check the console for details.');
    }
  };

  const handleStockAdjustment = async (adjustmentData) => {
    if (!adjustingProduct) return;
    try {
      await inventoryService.adjustStock(adjustingProduct._id, {
        ...adjustmentData,
        storeId: activeStore._id,
      });
      await fetchProducts(searchTerm, currentPage, filters);
      setIsStockModalOpen(false);
      setAdjustingProduct(null);
      setStockModalError('');
    } catch (err) {
      setStockModalError(err.response?.data?.message || 'Failed to adjust stock.');
    }
  };
  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product? This is a global action and will remove it from all stores.')) {
      try {
        await inventoryService.deleteProduct(id);
        setProducts(prevProducts => prevProducts.filter(p => p._id !== id));
      } catch (err) {
        setError('Failed to delete product.');
      }
    }
  };

  useEffect(() => {
    const topScroll = topInventoryScrollRef.current;
    const tableScroll = tableInventoryScrollRef.current;
    if (!topScroll || !tableScroll) return undefined;

    let isSyncing = false;
    let syncResetFrame = null;

    const updateScrollWidth = () => {
      setInventoryTableScrollWidth(tableScroll.scrollWidth);
    };

    const syncScroll = (source, target) => {
      if (isSyncing) return;
      isSyncing = true;
      target.scrollLeft = source.scrollLeft;
      if (syncResetFrame) {
        window.cancelAnimationFrame(syncResetFrame);
      }
      syncResetFrame = window.requestAnimationFrame(() => {
        isSyncing = false;
        syncResetFrame = null;
      });
    };

    const handleTopScroll = () => syncScroll(topScroll, tableScroll);
    const handleTableScroll = () => syncScroll(tableScroll, topScroll);

    updateScrollWidth();
    topScroll.addEventListener('scroll', handleTopScroll);
    tableScroll.addEventListener('scroll', handleTableScroll);
    window.addEventListener('resize', updateScrollWidth);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateScrollWidth);
      resizeObserver.observe(tableScroll);
      const tableElement = tableScroll.querySelector('table');
      if (tableElement) {
        resizeObserver.observe(tableElement);
      }
    }

    return () => {
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableScroll.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('resize', updateScrollWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (syncResetFrame) {
        window.cancelAnimationFrame(syncResetFrame);
      }
    };
  }, [products.length, canUpdate, canDelete]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          Inventory Management: <span className="text-blue-600">{activeStore?.name}</span>
        </h1>
        {canCreateProductWithPricing && (
          <button onClick={() => handleOpenModal()} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark transition">
            <FiPlusCircle className="mr-2" /> Add New Product
          </button>
        )}
      </div>

      <div className="mb-4">
        <form
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            setCurrentPage(1);
            fetchProducts(searchTerm, 1, filters);
      }}
        >
          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
            <FiSearch className="text-gray-400" />
          </span>
          <input
            type="text"
            placeholder="Search by name, SKU, or barcode..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="mb-4 bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              value={filters.category}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  category: e.target.value,
                  subcategory: '',
                  noCategory: e.target.value ? false : prev.noCategory,
                }))
              }
              disabled={filters.noCategory}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"
            >
              <option value="">All categories</option>
              {categories
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((category) => (
                  <option key={category._id} value={category.name}>
                    {category.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Subcategory</label>
            <select
              value={filters.subcategory}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  subcategory: e.target.value,
                  noSubcategory: e.target.value ? false : prev.noSubcategory,
                }))
              }
              disabled={filters.noCategory || !filters.category || filters.noSubcategory}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"
            >
              <option value="">All subcategories</option>
              {categories
                .find(
                  (category) =>
                    category.name.trim().toLowerCase() === filters.category.trim().toLowerCase()
                )
                ?.subcategories?.slice()
                .sort((a, b) => a.localeCompare(b))
                .map((subcategory) => (
                  <option key={subcategory} value={subcategory}>
                    {subcategory}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label htmlFor="inventory-supplier-filter" className="block text-sm font-medium text-gray-700">
              Supplier
            </label>
            <select
              id="inventory-supplier-filter"
              value={filters.supplierId}
              onChange={(e) => {
                setCurrentPage(1);
                setFilters((prev) => ({
                  ...prev,
                  supplierId: e.target.value,
                }));
              }}
              className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">All suppliers</option>
              {suppliers
                .slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((supplier) => (
                  <option key={supplier._id} value={supplier._id}>
                    {supplier.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex items-end gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.noCategory}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    noCategory: e.target.checked,
                    category: '',
                    subcategory: '',
                  }))
                }
                className="h-4 w-4 text-primary border-gray-300 rounded"
              />
              No category
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.noSubcategory}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    noSubcategory: e.target.checked,
                    subcategory: '',
                  }))
                }
                disabled={filters.noCategory}
                className="h-4 w-4 text-primary border-gray-300 rounded disabled:opacity-50"
              />
              No subcategory
            </label>
          </div>
        </div>
      </div>

      {pagination.pages > 1 && (
        <div className="mb-4 flex items-center justify-end text-sm text-gray-600 space-x-2">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage <= 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.pages} ({pagination.total} products)
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(pagination.pages, prev + 1))}
            disabled={currentPage >= pagination.pages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-semibold text-primary">Loading products…</p>
            </div>
          </div>
        )}
        <div className="bg-white shadow-md rounded-lg">
          <div
            ref={topInventoryScrollRef}
            role="region"
            aria-label="Inventory table horizontal scroll"
            tabIndex={0}
            className="h-4 overflow-x-auto overflow-y-hidden rounded-t-lg border-b border-gray-100 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <div style={{ width: inventoryTableScrollWidth || 1120, height: 1 }} />
          </div>
          <div
            ref={tableInventoryScrollRef}
            role="region"
            aria-label="Inventory table data"
            tabIndex={0}
            className="overflow-x-auto focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <table className="min-w-[1120px] w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
              <tr>
                <th className={`${compactHeaderClassName} w-[260px]`}>Name</th>
                <th className={compactHeaderClassName}>SKU</th>
                <th className={compactHeaderClassName}>Barcode</th>
                <th className={compactHeaderClassName}>Category</th>
                <th className={compactHeaderClassName}>Subcategory</th>
                <th className={compactHeaderClassName}>Supplier</th>
                <th className={`${compactHeaderClassName} text-right`}>Price</th>
                <th className={`${compactHeaderClassName} text-right`}>Cost</th>
                <th className={`${compactHeaderClassName} text-right`}>Stock</th>
                {(canUpdate || canDelete) && <th className={actionHeaderClassName}>Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => {
              const pricing = resolvePricingDisplay(product);
              return (
              <tr key={product._id}>
                <td className="w-[260px] px-3 py-2 text-sm font-medium leading-tight text-slate-900">{product.name}</td>
                <td className={`${compactCellClassName} whitespace-nowrap font-mono`}>{product.sku}</td>
                <td className={`${compactCellClassName} whitespace-nowrap font-mono`}>{product.barcode}</td>
                <td className={`${compactCellClassName} whitespace-nowrap`}>
                  {typeof product.category === 'string' ? product.category : product.category?.name || '—'}
                </td>
                <td className={`${compactCellClassName} whitespace-nowrap`}>
                  {product.subcategory || '—'}
                </td>
                <td className={`${compactCellClassName} whitespace-nowrap`}>
                  {resolveSupplierName(product)}
                </td>
                <td className={`${compactNumericCellClassName} whitespace-nowrap`}>
                  <div className="font-semibold text-slate-900">
                    ${pricing.storePrice.toFixed(2)}
                  </div>
                  <div className={`text-xs ${pricing.hasStorePriceDelta ? 'text-blue-600 font-semibold' : 'text-slate-500'}`}>
                    Base ${pricing.basePrice.toFixed(2)}
                  </div>
                </td>
                <td className={`${compactNumericCellClassName} whitespace-nowrap`}>
                  <div className="font-semibold text-slate-900">
                    ${pricing.storeCost.toFixed(2)}
                  </div>
                  <div className={`text-xs ${pricing.hasStoreCostDelta ? 'text-blue-600 font-semibold' : 'text-slate-500'}`}>
                    Base ${pricing.baseCost.toFixed(2)}
                  </div>
                </td>
                <td className={`${compactNumericCellClassName} whitespace-nowrap`}>{product.stock}</td>
                {(canUpdate || canDelete) && (
                  <td className={actionCellClassName}>
                    <div className="flex justify-end gap-1">
                      {canUpdate && (
                        <button
                          onClick={() => handleOpenStockModal(product)}
                          className={compactActionButtonClassName}
                          title="Adjust Stock"
                          aria-label={`Adjust stock for ${product.name}`}
                        >
                          <FiSliders size={16} />
                        </button>
                      )}
                      {canUpdate && (
                        <button
                          onClick={() => handleOpenLabelModal(product)}
                          className={compactActionButtonClassName}
                          title="Print Label"
                          aria-label={`Print label for ${product.name}`}
                        >
                          <FiPrinter size={16} />
                        </button>
                      )}
                      {canUpdate && (
                        <button
                          onClick={() => handleOpenModal(product)}
                          className={`${compactActionButtonClassName} text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-900`}
                          title="Edit Product"
                          aria-label={`Edit ${product.name}`}
                        >
                          <FiEdit size={16} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDeleteProduct(product._id)}
                          className={`${compactActionButtonClassName} text-red-600 hover:border-red-200 hover:bg-red-50 hover:text-red-900`}
                          title="Delete Product"
                          aria-label={`Delete ${product.name}`}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
            })}
            </tbody>
          </table>
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t text-sm text-gray-600">
            <span>
              Page {pagination.page} of {pagination.pages} ({pagination.total} products)
            </span>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(pagination.pages, prev + 1))}
                disabled={currentPage >= pagination.pages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl">
            <h2 className="text-2xl font-bold mb-4">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            <ProductForm
              product={editingProduct}
              store={activeStore}
              canUpdateMasterPrice={canUpdateMasterPrice}
              canUpdateMasterCost={canUpdateMasterCost}
              canUpdateStorePrice={canUpdateStorePrice}
              onSave={handleSaveProduct}
              onCancel={handleCloseModal}
            />
          </div>
        </div>
      )}

      {isStockModalOpen && adjustingProduct && (
        <StockAdjustmentModal
          product={adjustingProduct}
          apiError={stockModalError}
          onSave={handleStockAdjustment}
          onCancel={() => {
            setIsStockModalOpen(false);
            setAdjustingProduct(null);
            setStockModalError('');
          }}
        />
      )}

      {isLabelModalOpen && labelProduct && (
        <LabelPrintModal
          product={labelProduct}
          store={activeStore}
          onClose={() => {
            setIsLabelModalOpen(false);
            setLabelProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default InventoryPage;
